import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useContext,
} from 'react';
import { Button, Form, Input, Radio, Steps, Tooltip, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { SyncOutlined } from '@ant-design/icons';
import { LorawanActionTypes } from '@utils/deviceActions';
import api from '@services/api';
import { hexadecimalGenerator } from '@utils/hexadecimalGenerator';
import SessionContext from '@providers/Session/SessionContext';
import { useNavigate } from 'react-router-dom';
import cleanObject from '@utils/cleanObject';
import DetailsStep, {
  DetailsStepDataProps,
} from './ActivationModalSteps/DetailsStep';
import ConfigStep, {
  ConfigStepDataProps,
} from './ActivationModalSteps/ConfigStep';
import TypeStep, { TypeStepDataProps } from './ActivationModalSteps/TypeStep';

import * as S from './styles';
import { LoraWanActivationTypes } from './ActivationButton';
import ActivationToken from './ActivationToken';

const { Item } = Form;

export interface DeviceProps {
  activation?: 'OTAA' | 'ABP';
  encryption?: 'NS' | 'APP';
  devEui?: string;
  appEui?: string;
  description?: string;
  tags?: string[] | string;
  appKey?: string;
  devAddr?: string;
  nwksKey?: string;
  appsKey?: string;
  blockUplink?: boolean;
  blockDownlink?: boolean;
  devClass?: string;
  countersSize?: string;
  adr?: {
    mode: 'on' | 'off' | 'static';
    txPower: string;
    datarate: string;
  };
  band?: string;
}

interface ActivationPayloadProps {
  action: LorawanActionTypes.ACTIVATION;
  contractId?: string;
  customerId?: number;
  accountId?: number;
  contractIdMOT?: string;
  devices: DeviceProps[];
}

interface ActivationModalProps {
  visible: boolean;
  activationType: LoraWanActivationTypes;
  onClose: () => void;
}

const ActivationModal = ({
  visible,
  activationType,
  onClose,
}: ActivationModalProps) => {
  const navigate = useNavigate();
  const { t } = useTranslation(['lorawan', 'device-actions-modal']);
  const { selectedApp, isMot } = useContext(SessionContext);
  const [...forms] = [...Form.useForm(), ...Form.useForm(), ...Form.useForm()];

  const isBatch = ['batch', 'token'].includes(activationType);
  const isToken = activationType === 'token';

  const [saveLoading, setSaveLoading] = useState(false);
  const [step, setStep] = useState(0);
  const [uploadedDevices, setUploadedDevices] = useState<
    DeviceProps[] | undefined
  >();
  const [typeValues, setTypeValues] = useState<TypeStepDataProps>();
  const [detailsValues, setDetailsValues] = useState<DetailsStepDataProps>();
  const [activation, setActivation] =
    useState<TypeStepDataProps['activation']>();
  const [encryption, setEncryption] =
    useState<TypeStepDataProps['encryption']>();

  useEffect(() => {
    if (forms?.[0]) {
      setActivation(forms[0].getFieldValue('activation'));
      setEncryption(forms[0].getFieldValue('encryption'));
    }
  }, [forms]);

  const postActivation = useCallback(
    (payload: any) => api.post('/service-proxy/lorawan/actions', payload),
    [],
  );

  const renderItemLabel = useCallback(
    (label: string, isRequired?: boolean) => (
      <>
        {isRequired && <span style={{ color: 'red' }}>*&nbsp;</span>}
        {t(label)}
      </>
    ),
    [t],
  );

  const renderLabelWithHexadecimalGenerate = useCallback(
    (label: string, numSize?: number) => (
      <>
        {renderItemLabel(label)}
        {numSize && (
          <Tooltip title={t('generateHexadecimal')}>
            <Button
              icon={<SyncOutlined />}
              type="link"
              size="small"
              onClick={() => {
                forms?.[step].setFieldValue(
                  label,
                  hexadecimalGenerator(numSize),
                );
              }}
            />
          </Tooltip>
        )}
      </>
    ),
    [forms, renderItemLabel, step, t],
  );

  const validateLength = useCallback(
    (value: string | undefined, size?: number) =>
      value && value.length === size
        ? Promise.resolve()
        : Promise.reject(new Error(t('fieldSize', { size }))),
    [t],
  );

  const renderInputItem = useCallback(
    (name: string, numSize?: number, validateNumSize?: boolean) => (
      <Item
        label={
          numSize
            ? renderLabelWithHexadecimalGenerate(name, numSize)
            : renderItemLabel(name)
        }
        name={name}
        rules={[
          {
            required: true,
            message: t('fieldIsRequired'),
          },
          ...(validateNumSize
            ? [
                {
                  validator: (_: any, value: string | undefined) =>
                    validateLength(value, numSize),
                },
              ]
            : []),
        ]}
      >
        <Input allowClear />
      </Item>
    ),
    [renderItemLabel, renderLabelWithHexadecimalGenerate, t, validateLength],
  );

  const renderRadioItem = useCallback(
    (name: string, options: string[]) => (
      <Item
        label={t(name)}
        name={name}
        rules={[
          {
            required: true,
            message: t('fieldIsRequired'),
          },
        ]}
      >
        <Radio.Group defaultValue={options?.[0]} buttonStyle="solid">
          {options?.map(opt => (
            <Radio.Button key={opt} value={opt}>
              {opt}
            </Radio.Button>
          ))}
        </Radio.Group>
      </Item>
    ),
    [t],
  );

  const handleClose = useCallback(() => {
    forms.forEach(f => f.resetFields());
    setStep(0);
    setUploadedDevices(undefined);
    onClose();
  }, [forms, onClose]);

  const payloadToSend = useCallback(
    (configValues: ConfigStepDataProps): ActivationPayloadProps => {
      const defaultPayload: ActivationPayloadProps = {
        action: LorawanActionTypes.ACTIVATION,
        contractId: detailsValues?.contractId,
        devices: [],
      };

      const {
        requestId,
        contractMotId,
        contractLorawanId,
        ...detailsValuesRest
      } = detailsValues || {};

      if (isMot) {
        if (requestId) {
          // activation made from broker children
          defaultPayload.accountId = requestId;
        } else {
          // activation made from broker
          defaultPayload.contractIdMOT = contractMotId;
          defaultPayload.contractId = contractLorawanId;
        }
      }

      const deviceDefaultValues = {
        ...typeValues,
        ...configValues,
        band: 'LA915-928A',
      };

      let devices = [];

      if (isBatch) {
        devices =
          uploadedDevices?.map(device =>
            cleanObject({
              ...deviceDefaultValues,
              ...device,
              tags: `${device?.tags}`.split(';'),
            }),
          ) ?? [];
      } else {
        devices = detailsValuesRest
          ? [
              {
                ...deviceDefaultValues,
                ...detailsValuesRest,
              },
            ]
          : [];
      }
      return {
        ...defaultPayload,
        // @ts-ignore
        devices,
      };
    },
    [detailsValues, isBatch, isMot, typeValues, uploadedDevices],
  );

  const handleSuccess = useCallback(() => {
    const navigatePath = `/${selectedApp}${
      isMot ? '/lorawan' : ''
    }/operational/actions-queue?actionType=0`;
    message.success(
      <>
        <span>{t('device-actions-modal:msgs.requested-action')}</span>
        <Button
          type="link"
          style={{ margin: '0 4px', padding: 0 }}
          onClick={() => navigate(navigatePath)}
        >
          {t('device-actions-modal:msgs.follow-in-the-actions-queue')}
        </Button>
      </>,
      2,
    );
  }, [navigate, selectedApp, isMot, t]);

  const handleFormFinish = useCallback(
    async (payload: ActivationPayloadProps) => {
      setSaveLoading(true);
      try {
        await postActivation(payload);
        handleSuccess();
        handleClose();
      } catch {
        message.error(t('errorWhenActivating'));
      }
      setSaveLoading(false);
    },
    [postActivation, handleSuccess, handleClose, t],
  );

  const formValidate = useCallback(
    () =>
      forms[step].validateFields().then(data => {
        forms[step].submit();
        return data;
      }),
    [forms, step],
  );

  const handleStep = useCallback(
    (action: 'back' | 'next' | 'confirm') => {
      if (action === 'back') {
        setStep(oldStep => oldStep - 1);
      } else if (action === 'next') {
        formValidate().then(values => {
          if (step === 0) {
            setTypeValues(values);
          } else {
            setDetailsValues(values);
          }
          setStep(oldStep => oldStep + 1);
        });
      } else if (action === 'confirm') {
        formValidate().then(values => {
          handleFormFinish(payloadToSend(values));
        });
      }
    },
    [formValidate, handleFormFinish, payloadToSend, step],
  );

  const modalSteps = useMemo(
    () => [
      {
        name: 'type',
        content: (
          <TypeStep key={0} form={forms[0]} renderRadioItem={renderRadioItem} />
        ),
      },
      {
        name: 'details',
        content: (
          <DetailsStep
            key={1}
            form={forms[1]}
            isBatch={isBatch}
            isToken={isToken}
            onUpload={setUploadedDevices}
            activation={activation}
            encryption={encryption}
            renderItemLabel={renderItemLabel}
            renderInputItem={renderInputItem}
          />
        ),
      },
      {
        name: 'config',
        content: (
          <ConfigStep
            key={2}
            form={forms[2]}
            renderItemLabel={renderItemLabel}
            renderRadioItem={renderRadioItem}
            isBatch={isBatch}
          />
        ),
      },
    ],
    [
      forms,
      renderRadioItem,
      isBatch,
      isToken,
      activation,
      encryption,
      renderItemLabel,
      renderInputItem,
    ],
  );

  const stepsTitles = useMemo(
    () => modalSteps?.map(({ name }) => ({ title: t(name) })),
    [modalSteps, t],
  );

  if (isToken) {
    return (
      <ActivationToken
        open={visible}
        onClose={onClose}
        renderInputItem={renderInputItem}
        renderItemLabel={renderItemLabel}
      />
    );
  }

  return (
    <S.ModalContainer
      bodyStyle={{ padding: '24px 40px' }}
      afterClose={() => document.body.style.removeProperty('overflow')}
      footer={
        <>
          {step > 0 && (
            <Button onClick={() => handleStep('back')}>
              {t('buttons.back')}
            </Button>
          )}

          {step === 2 ? (
            <Button
              type="primary"
              onClick={() => handleStep('confirm')}
              loading={saveLoading}
            >
              {t('buttons.confirm')}
            </Button>
          ) : (
            <Button type="primary" onClick={() => handleStep('next')}>
              {t('buttons.next')}
            </Button>
          )}
        </>
      }
      maskClosable={false}
      title={t(`${activationType}Activation`)}
      open={visible}
      centered
      forceRender
      onCancel={() => {
        document.body.style.removeProperty('overflow');
        handleClose();
      }}
    >
      <Steps
        size="small"
        current={step}
        items={stepsTitles}
        style={{ marginBottom: 48 }}
      />

      {modalSteps?.[step]?.content}
    </S.ModalContainer>
  );
};

export default ActivationModal;
